import { useTranslation } from "react-i18next";
import BtnCustom from "components/common/btn-custom";
import CustomTable from "components/ReactTable/CustomTable";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "reactstrap";
import { GetDataSet } from "network/wineDataSetAPI";
import { ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { DeleteDataSet } from "network/wineDataSetAPI";
import { successToaster } from "components/common/toast";
import ReactBSAlert from "react-bootstrap-sweetalert";
import TableFilter from "components/TableFilter/TableFilter";
import CountryList from "country-list-with-dial-code-and-flag";

export default function WineDataSetTable() {
  const { t } = useTranslation("wine");
  const navigate = useNavigate();
  const [dataSet, setDataSet] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState([]);
  const [filter, setFilter] = useState([]);
  const [total, setTotal] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(false);

  const pageSize = 10;

const DATASET_FILTERS = [
  { value: "", label: t("list.filterAll") },
  { value: "active", label: t("list.filterActive") },
  { value: "inactive", label: t("list.filterInactive") },
  { value: "no_ean", label: t("list.filterNoEan") },
  { value: "no_vintage", label: t("list.filterNoVintage") },
];

  const handleDelete = async (id) => {
    try {
      const resDelete = await DeleteDataSet(id);
      if (resDelete.success) {
        successToaster(t("list.deleted"));
        setDataSet((prevDataSet) =>
          prevDataSet.filter((value) => value?.uuid !== id)
        );
        hideAlert();
      }
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
      hideAlert();
    }
  };

  const warningWithConfirmMessage = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("common.confirmTitle")}
        onConfirm={() => handleDelete(id)}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("common.confirmYes")}
        cancelBtnText={t("common.cancel")}
        showCancel
        btnSize=""
      >
        {t("list.confirmDelete")}
      </ReactBSAlert>
    );
  };
  const hideAlert = () => {
    setAlert(null);
  };
  const columns = useMemo(
    () => [
      {
        Header: t("fields.ean"),
        accessor: "ean",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("list.colName"),
        accessor: "name",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("list.colCountry"),
        accessor: "country",
        Cell: ({ value }) => {
          if (!value || typeof value !== "string") return "-";
          const country = CountryList.findOneByCountryCode(value);
          return country?.name || "-";
        },
      },
      {
        Header: t("list.colRegion"),
        accessor: "region",
        Cell: ({ value }) => value || "-",
      },

      {
        Header: t("list.colCreated"),
        accessor: "created_at",
        Cell: ({ value }) =>
          value
            ? new Date(value).toLocaleString("it-IT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
      },
      {
        Header: t("list.colActive"),
        accessor: "is_active",
        Cell: ({ value }) => (value ? t("list.active") : t("list.inactive")),
      },

      {
        Header: t("common.actions"),
        accessor: "Action",
        Cell: ({ row }) => (
          <div className="d-flex  align-items-center">
            <i
              className="mr-3 pointer"
              onClick={() =>
                navigate(`/wine-dataset/view/${row?.original?.uuid}`)
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 576 512"
                width={20}
                height={20}
                className="eyeIcon"
              >
                <path d="M288 144a110.9 110.9 0 0 0 -31.2 5 55.4 55.4 0 0 1 7.2 27 56 56 0 0 1 -56 56 55.4 55.4 0 0 1 -27-7.2A111.7 111.7 0 1 0 288 144zm284.5 97.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400c-98.7 0-189.1-55-237.9-144C98.9 167 189.3 112 288 112s189.1 55 237.9 144C477.1 345 386.7 400 288 400z" />
              </svg>
            </i>
            <i
              className="tim-icons icon-pencil mr-3 text-warning pointer"
              onClick={() =>
                navigate(`/wine-dataset/edit/${row?.original?.uuid}`)
              }
            ></i>
            <i
              className="tim-icons icon-trash-simple text-danger pointer"
              onClick={() => warningWithConfirmMessage(row?.original?.uuid)}
            ></i>
          </div>
        ),
      },
    ],
    [t]
  );

  async function getData() {
    try {
      setIsLoading(true);
      const response = await GetDataSet(
        currentPage,
        pageSize,
        search,
        sort?.value,
        filter?.value
      );
      if (response.success) {
        setIsLoading(false);
        setDataSet(response?.data?.winedata);
        setTotalPages(response?.data?.total_pages);
        setTotal(response?.data?.total_count);
      }
    } catch (errors) {
      // non lasciare le righe della pagina precedente: sembrerebbero quelle di questa
      setIsLoading(false);
      setDataSet([]);
      ErrorToaster(handleError(errors?.status_code, errors?.message));
    }
  }
  useEffect(() => {
    setCurrentPage(1);
    setFetchTrigger((prev) => !prev);
  }, [sort, search, filter]);

  useEffect(() => {
    getData();
  }, [currentPage, fetchTrigger]);
  return (
    <div className="content my-4">
      <Card className="p-4">
        {alert}
        <div className=" d-flex flex-wrap justify-content-between align-items-center  mb-2">
          <h2 className="card-title text-capitalize">{t("list.title")}</h2>
          <BtnCustom
            color="btn btn-primary"
            onClick={() => navigate(`/wine-dataset/add`)}
          >
            {t("list.add")}
          </BtnCustom>
        </div>

        <TableFilter
          setSearch={setSearch}
          search={search}
          showSearch
          showSort
          setSort={setSort}
          sort={sort}
          showFilter
          filter={filter}
          setFilter={setFilter}
          filterOptionsList={DATASET_FILTERS}
          total={total}
          totalLabel={t("list.totalLabel")}
        />
        <CustomTable
          data={dataSet}
          columns={columns}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          showPagination={totalPages > 1}
          isLoading={isLoading}
        />
      </Card>
    </div>
  );
}
